import { PortfolioTab } from './Portfolio';
import { EvaluatorLinks, TestsCard } from './Evaluations';
import { FieldTripsCard } from './FieldTrips';
import { CloseOutNudge } from './CloseOut';
import { TranscriptCard } from './Transcript';
import { ArrowLeft, BookOpen, Info, Landmark, Lock, MonitorPlay, PenLine, UserRound, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, Navigate, NavLink, useParams, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import type { Child } from '../lib/types';
import { Avatar } from '../ui/Avatar';
import { QuickActions } from './Actions';
import { ActivityList } from './ActivityList';
import { DevicesPanel } from './DevicesPanel';
import { LiveView } from './LiveView';
import { useParent } from './ParentContext';
import { ScheduleEditor } from './ScheduleEditor';
import { SiteRules } from './SiteRules';
import { StudentProfile } from './StudentProfile';
import { PolicyBadge, Sharing } from './StatusBits';
import { Whiteboard } from './Whiteboard';
import { ChatThread, type ChatMsg } from './Chat';
import { GradeBook } from './GradeBook';
import { AttendanceTab } from './AttendanceTab';
import { StateCompliancePage, type CompliancePage } from './StateCompliance';
import { ReadingLog } from './ReadingLog';
import { Planner } from './Planner';
import { SubjectsContext } from '../ui/SubjectField';
import { useCallback, useEffect } from 'react';
import { canDo, childHides, planHas, PlanLocked } from './Plans';
import { CopySettingsButton } from './CopySettings';
import { ChildFeatures } from './ChildFeatures';
import type { FeatureKey } from '../lib/types';

// The child's pages, grouped into sections so the tab bar stays short (#301): the sections on the first row, the
// current section's pages on a second. Page ids are the URL segment (/kids/:id/:tab), unchanged from before. Student
// Oversight and Student became one Student section (#500), most-used page first.
const SECTIONS = [
  {
    id: 'live',
    label: 'Collaboration',
    icon: MonitorPlay,
    tabs: [
      { id: 'live', label: 'Live view' },
      { id: 'whiteboard', label: 'Whiteboard' },
      { id: 'chat', label: 'Chat' },
    ],
  },
  {
    id: 'schoolwork',
    label: 'Learning Hub',
    icon: BookOpen,
    tabs: [
      { id: 'planner', label: 'Planner' },
      { id: 'grades', label: 'Grade Book' },
      { id: 'attendance', label: 'Attendance' },
      { id: 'reading', label: 'Reading Log' },
      { id: 'portfolio', label: 'Portfolio' },
    ],
  },
  {
    // State compliance, its own section (#954): off until the family opts in, then progress, deadlines and documents.
    id: 'compliance',
    label: 'State compliance',
    icon: Landmark,
    tabs: [
      { id: 'compliance', label: 'Progress' },
      { id: 'deadlines', label: 'Deadlines & documents' },
      { id: 'filing', label: 'Where you file' },
    ],
  },
  {
    id: 'student',
    label: 'Student',
    icon: UserRound,
    tabs: [
      { id: 'activity', label: 'Online Activity' },
      { id: 'sites', label: 'Safety Controls' },
      { id: 'schedule', label: 'Schedule' },
      { id: 'features', label: 'Features' },
      { id: 'devices', label: 'Devices' },
      { id: 'profile', label: 'Profile' },
    ],
  },
] as const;

type Section = (typeof SECTIONS)[number];
const COMPLIANCE_TABS: string[] = ['compliance', 'deadlines', 'filing'];

/** The page last opened in a section, so going back to a section returns there (remembered in this browser). */
const lastTabKey = (section: string) => `cg.childTab.${section}`;
function lastTab(section: Section): string {
  try {
    const t = localStorage.getItem(lastTabKey(section.id));
    if (t && section.tabs.some((x) => x.id === t)) return t;
  } catch {
    /* storage unavailable */
  }
  return section.tabs[0].id;
}

export function ChildPage() {
  const { id = '', tab = 'live' } = useParams();
  const section = SECTIONS.find((s) => s.tabs.some((t) => t.id === tab)) ?? SECTIONS[0];
  // Remember this page as the section's last one, for the section tab's link.
  useEffect(() => {
    try {
      localStorage.setItem(lastTabKey(section.id), tab);
    } catch {
      /* storage unavailable */
    }
  }, [section.id, tab]);
  const { children, loaded, parent } = useParent();
  const child = children.find((c) => c.id === id);
  // A link to a child in another of your families (#652), like a notification: switch to that family and open it.
  const [switching, setSwitching] = useState(false);
  const multi = (parent.families?.length ?? 0) > 1;
  useEffect(() => {
    if (child || !loaded || !multi) return;
    let live = true;
    api
      .get<Child>(`/api/children/${id}`)
      .then(async (c) => {
        if (!live || !c.familyId || c.familyId === parent.activeFamilyId) return;
        setSwitching(true);
        await api.put('/api/me/active-family', { familyId: c.familyId });
        window.location.reload();
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [child, loaded, multi, id, parent.activeFamilyId]);

  if (!child) {
    return (
      <div className="page">
        <p className="muted">{switching ? 'Opening their family…' : loaded ? 'That child isn’t in your family.' : 'Loading…'}</p>
        <Link to="/">Back to the family</Link>
      </div>
    );
  }

  // Plans (#99): the tabs whose feature the child's family plan doesn't include.
  const TAB_FEATURES: Record<string, { feature: FeatureKey; label: string }> = {
    live: { feature: 'live_view', label: 'Live view' },
    whiteboard: { feature: 'whiteboard', label: 'The whiteboard' },
    chat: { feature: 'chat', label: 'Chat' },
    grades: { feature: 'grade_book', label: 'The grade book' },
    attendance: { feature: 'attendance', label: 'Attendance' },
    compliance: { feature: 'state_compliance', label: 'State compliance' },
    deadlines: { feature: 'state_compliance', label: 'State compliance' },
    filing: { feature: 'state_compliance', label: 'State compliance' },
    reading: { feature: 'reading_log', label: 'The reading log' },
    portfolio: { feature: 'portfolio', label: 'The portfolio' },
    // A records-only plan (#564) has no app on the child's computer.
    devices: { feature: 'computer', label: 'The app on your child’s computer' },
  };
  const locked = (t: string) => !!TAB_FEATURES[t] && !planHas(child, TAB_FEATURES[t].feature);
  const lockLabel = (t: string) => TAB_FEATURES[t]!.label;
  // Pages the parent hid for this child (#454) go away, and a section with nothing left goes too.
  const TAB_KEY: Record<string, string> = { live: 'live_view', whiteboard: 'whiteboard', chat: 'chat', planner: 'planner', grades: 'grade_book', attendance: 'attendance', reading: 'reading_log', portfolio: 'portfolio' };
  // ...and pages in an area your permissions don't cover (#471): co-parents see only what the owner allows.
  const TAB_AREA: Record<string, string> = {
    live: 'live_view', whiteboard: 'whiteboard', chat: 'chat', planner: 'planner', grades: 'grade_book', attendance: 'attendance',
    reading: 'reading_log', portfolio: 'grade_book', activity: 'activity', sites: 'child_settings', devices: 'child_settings', profile: 'child_settings',
    schedule: 'child_settings', features: 'child_settings', compliance: 'attendance', deadlines: 'attendance', filing: 'attendance',
  };
  // A records-only plan (#564): no computer, so nothing to watch or block.
  const hasComputer = planHas(child, 'computer');
  const hiddenTab = (t: string) =>
    (!!TAB_KEY[t] && childHides(child, TAB_KEY[t])) || (!!TAB_AREA[t] && !canDo(child, TAB_AREA[t], 'view')) || (!hasComputer && (t === 'activity' || t === 'sites')) ||
    // State compliance (#954): only where it's offered; its other pages only once the family has turned it on.
    (COMPLIANCE_TABS.includes(t) && !child.stateCompliance?.available) ||
    ((t === 'deadlines' || t === 'filing') && !child.stateCompliance?.on);
  const sections = SECTIONS.map((s) => ({ ...s, tabs: s.tabs.filter((t) => !hiddenTab(t.id)) })).filter((s) => s.tabs.length);
  const shown = sections.find((s) => s.id === section.id) ?? sections[0]!;
  if (hiddenTab(tab)) return <Navigate to={`/kids/${child.id}/${shown.tabs[0]!.id}`} replace />;

  return (
    <SubjectsContext.Provider value={child.subjects ?? []}>
      <div className="page">
        <header className="child-head">
          <Link to="/" className="icon-btn back" aria-label="Back to the family">
            <ArrowLeft size={20} />
          </Link>
          <Avatar name={child.name} color={child.color} size={52} />
          <div className="grow">
            <h1>{child.name}</h1>
            {hasComputer && <PolicyBadge policy={child.policy} />}
          </div>
          {canDo(child, 'child_settings', 'view') && <CopySettingsButton child={child} />}
        </header>

        {hasComputer && canDo(child, 'school_time', 'edit') && (
          <>
            <div className="child-bar">
              <ModeSwitch child={child} />
              <QuickActions child={child} />
            </div>
            <DoneForDay child={child} />
          </>
        )}

        <nav className="tabs section-tabs" role="tablist" aria-label="Sections">
          {sections.map((s) => {
            const Icon = s.icon;
            const on = s.id === section.id;
            const firstTab = s.tabs.some((t) => t.id === lastTab(SECTIONS.find((x) => x.id === s.id)!)) ? lastTab(SECTIONS.find((x) => x.id === s.id)!) : s.tabs[0]!.id;
            return (
              <NavLink key={s.id} to={`/kids/${child.id}/${on ? tab : firstTab}`} className={() => (on ? 'active' : '')} role="tab" aria-selected={on}>
                <Icon size={17} aria-hidden="true" /> {s.label}
              </NavLink>
            );
          })}
        </nav>
        {shown.tabs.length > 1 && (
          <nav className="subtabs" role="tablist" aria-label={shown.label}>
            {shown.tabs.map((t) => (
              <NavLink key={t.id} to={`/kids/${child.id}/${t.id}`} className={() => (tab === t.id ? 'active' : '')} role="tab" aria-selected={tab === t.id}>
                {t.label}
                {locked(t.id) && <Lock size={12} className="tab-lock" aria-label="Not in your plan" />}
              </NavLink>
            ))}
          </nav>
        )}
        {section.id === 'live' && !childHides(child, 'live_view') && !child.devices.some((d) => d.kind === 'agent') && (
          <p className="notice with-icon collab-notice">
            <Info size={16} /> Live view and the whiteboard need {child.name}’s computer. They’ll be available once a computer is paired on the{' '}
            <Link to={`/kids/${child.id}/devices`}>Devices</Link> page.
          </p>
        )}

        {tab === 'live' && (locked('live') ? <PlanLocked label={lockLabel('live')} /> : <LiveTab child={child} canBoard={!locked('whiteboard') && !hiddenTab('whiteboard')} />)}
        {tab === 'whiteboard' && (locked('whiteboard') ? <PlanLocked label={lockLabel('whiteboard')} /> : <WhiteboardTab child={child} canLive={!locked('live') && !hiddenTab('live')} />)}
        {tab === 'chat' && (locked('chat') ? <PlanLocked label={lockLabel('chat')} /> : <ChatTab child={child} />)}
        {tab === 'planner' && <Planner child={child} />}
        {tab === 'grades' && (locked('grades') ? <PlanLocked label={lockLabel('grades')} /> : <GradeBook child={child} />)}
        {tab === 'attendance' && (locked('attendance') ? <PlanLocked label={lockLabel('attendance')} /> : <AttendanceTab child={child} />)}
        {COMPLIANCE_TABS.includes(tab) &&
          (locked(tab) ? <PlanLocked label={lockLabel(tab)} /> : <StateCompliancePage child={child} page={tab as CompliancePage} />)}
        {tab === 'reading' && (locked('reading') ? <PlanLocked label={lockLabel('reading')} /> : <ReadingLog child={child} />)}
        {tab === 'portfolio' && (locked('portfolio') ? <PlanLocked label={lockLabel('portfolio')} /> : (
            <>
              <CloseOutNudge childName={child.name} />
              <PortfolioTab child={child} />
              <FieldTripsCard child={child} />
              <TestsCard child={child} />
              <TranscriptCard child={child} />
              <EvaluatorLinks child={child} />
            </>
          ))}
        {tab === 'activity' && <ActivityList child={child} />}
        {tab === 'schedule' && <ScheduleEditor child={child} />}
        {tab === 'sites' && <SiteRules child={child} />}
        {tab === 'devices' && (locked('devices') ? <PlanLocked label={lockLabel('devices')} /> : <DevicesPanel child={child} />)}
        {tab === 'profile' && <StudentProfile child={child} />}
        {tab === 'features' && <ChildFeatures child={child} />}
      </div>
    </SubjectsContext.Provider>
  );
}

/**
 * Done for today (#440): school time is off until tomorrow's schedule. Undo it, or, during school time, end the day
 * early without waiting for the child to ask.
 */
function DoneForDay({ child }: { child: Child }) {
  const { replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  async function run(call: () => Promise<Child>, done: string) {
    setBusy(true);
    try {
      replaceChild(await call());
      toast({ text: done, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  if (child.policy.doneForDay) {
    return (
      <div className="done-for-day row-between">
        <span>
          🎉 <strong>{child.name} is done for today.</strong> <span className="muted">School time starts again with tomorrow’s schedule.</span>
        </span>
        <button className="btn small" disabled={busy} onClick={() => run(() => api.del<Child>(`/api/children/${child.id}/done-for-day`), 'Back on the schedule for today.')}>
          Resume today
        </button>
      </div>
    );
  }
  if (!child.policy.schoolTime || child.mode === 'on') return null;
  return (
    <div className="done-for-day subtle row-between">
      <span className="muted small">Finished early? End school time for the rest of today. It starts again tomorrow.</span>
      <button
        className="btn small"
        disabled={busy}
        onClick={() => run(() => api.post<Child>(`/api/children/${child.id}/done-for-day`, { approve: true }), `School’s done for today for ${child.name}.`)}
      >
        Done for today
      </button>
    </div>
  );
}

function ModeSwitch({ child }: { child: Child }) {
  const { replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  const options = [
    { id: 'auto', label: 'Follow schedule' },
    { id: 'on', label: 'School now' },
    { id: 'off', label: 'No school' },
  ] as const;

  async function set(mode: Child['mode']) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { mode }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="segmented" role="radiogroup" aria-label="School time">
      {options.map((o) => (
        <button key={o.id} role="radio" aria-checked={child.mode === o.id} className={child.mode === o.id ? 'active' : ''} disabled={busy} onClick={() => set(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function LiveTab({ child, canBoard }: { child: Child; canBoard: boolean }) {
  const agents = child.devices.filter((d) => d.kind === 'agent');
  const [picked, setPicked] = useState<string | null>(null);
  // The whiteboard beside live view (#648); "Watch the screen too" on the Whiteboard tab opens it with ?board=1.
  const [params] = useSearchParams();
  const [board, setBoard] = useState(() => canBoard && params.get('board') === '1');
  const device = agents.find((d) => d.id === picked) ?? agents.find((d) => d.online) ?? agents[0];

  if (!device) {
    return (
      <div className="card empty">
        <h2>No computer paired yet</h2>
        <p className="muted">Pair {child.name}’s computer to see the screen, webcam and audio during school time.</p>
        <Link className="btn primary" to={`/kids/${child.id}/devices`}>
          Pair a computer
        </Link>
      </div>
    );
  }

  return (
    <>
      {agents.length > 1 && (
        <div className="device-picker">
          {agents.map((d) => (
            <button key={d.id} className={`chip ${d.id === device.id ? 'active' : ''}`} onClick={() => setPicked(d.id)}>
              <span className={`dot ${d.online ? 'on' : ''}`} /> {d.name}
            </button>
          ))}
        </div>
      )}
      <div className="live-meta">
        <span>
          <span className={`dot ${device.online ? 'on' : ''}`} /> {device.name}
        </span>
        <span className="row-gap">
          <Sharing device={device} />
          {canBoard && (
            <button className={`btn small${board ? ' board-on' : ''}`} onClick={() => setBoard((b) => !b)} aria-pressed={board}>
              <PenLine size={15} /> {board ? 'Close whiteboard' : 'Whiteboard'}
            </button>
          )}
        </span>
      </div>
      <div className={board ? 'live-split' : undefined}>
        <div className="live-split-main">
          <LiveView key={device.id} child={child} device={device} />
        </div>
        {board && <WhiteboardPanel child={child} onClose={() => setBoard(false)} />}
      </div>
      <p className="muted small">
        {child.name} sees a “watching” notice while you watch, and hears you only while you talk.
      </p>
    </>
  );
}

/** The parent's half of the shared whiteboard: tells the child's devices to open it, then syncs strokes. */
function WhiteboardTab({ child, canLive }: { child: Child; canLive: boolean }) {
  const agents = canLive ? child.devices.filter((d) => d.kind === 'agent') : [];
  const watch = agents.length > 0;
  // Talk while you draw (#863): live view's talking, without the screen.
  const talkTo = agents.find((d) => d.online) ?? agents[0];
  return (
    <WhiteboardPanel
      child={child}
      top={talkTo && <LiveView key={talkTo.id} child={child} device={talkTo} voiceOnly />}
      extra={
        watch && (
          <Link className="btn small" to={`/kids/${child.id}/live?board=1`}>
            <MonitorPlay size={15} /> Watch the screen too
          </Link>
        )
      }
    />
  );
}

/**
 * The shared whiteboard (#648: also beside live view): tells the child's devices to open it while it's shown, and closes
 * it when it goes.
 */
function WhiteboardPanel({ child, onClose, extra, top }: { child: Child; onClose?: () => void; extra?: ReactNode; top?: ReactNode }) {
  const { socket, connected } = useParent();
  const send = useCallback((payload: object) => socket?.send({ type: 'whiteboard', childId: child.id, ...payload }), [socket, child.id]);
  // Only this child's board (#730): with two children's boards open, each keeps to its own.
  const subscribe = useCallback(
    (fn: (m: { type: string; [k: string]: unknown }) => void) => socket?.on('whiteboard', (m: { type: string; [k: string]: unknown }) => m.childId === child.id && fn(m)) ?? (() => {}),
    [socket, child.id],
  );

  // Ask the child's devices to show the board while this tab is open; hide it when we leave.
  useEffect(() => {
    send({ open: true });
    return () => {
      send({ close: true });
    };
  }, [send]);

  const online = child.devices.some((d) => d.online);
  return (
    <section className="card stack whiteboard-panel">
      <div className="row-between wrap">
        <h2>Whiteboard with {child.name}</h2>
        <span className="row-gap">
          {extra}
          {onClose && (
            <button className="icon-btn" onClick={onClose} aria-label="Close the whiteboard">
              <X size={16} />
            </button>
          )}
        </span>
      </div>
      <p className="muted small">
        A shared blank board — you and {child.name} both draw here, live. {online ? 'The board is open on their computer.' : `Open on their computer when ${child.name}’s app is online.`}
      </p>
      {!connected && <p className="muted small">Reconnecting…</p>}
      {top}
      <Whiteboard send={send} subscribe={subscribe} label={`${child.name} sees the same board and can draw too.`} />
    </section>
  );
}

/** Text chat with the child — a fallback when live view or talking isn't available. */
function ChatTab({ child }: { child: Child }) {
  const { socket, connected, parent, toast } = useParent();
  const [messages, setMessages] = useState<ChatMsg[]>([]);

  // Seeing the thread marks it read for the family screen's unread count (#169).
  const markRead = useCallback(() => {
    api.post(`/api/children/${child.id}/chat/read`).catch(() => undefined);
  }, [child.id]);

  // Read the thread when the tab opens, again after every reconnect, and when the page comes back to the screen
  // (#859): a phone that slept misses what was sent meanwhile.
  const load = useCallback(() => {
    api
      .get<{ messages: ChatMsg[] }>(`/api/children/${child.id}/chat`)
      .then((r) => {
        setMessages(r.messages);
        markRead();
      })
      .catch(() => {});
  }, [child.id, markRead]);
  useEffect(() => {
    if (connected) load();
  }, [load, connected]);
  useEffect(() => {
    load();
    const back = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [load]);

  // Sent as a plain request (#859), so a dropped connection shows an error instead of losing the message.
  const send = async (text: string) => {
    try {
      const r = await api.post<{ message: ChatMsg }>(`/api/children/${child.id}/chat`, { text });
      setMessages((prev) => (prev.some((x) => x.id === r.message.id) ? prev : [...prev, r.message]));
      return true;
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : 'Your message didn’t send. Try again.', tone: 'alert' });
      return false;
    }
  };

  useEffect(() => {
    if (!socket) return;
    return socket.on('chat', (m) => {
      if (m.childId !== child.id) return;
      if (m.from === 'child') markRead(); // arrived while the chat is open
      const msg: ChatMsg = {
        id: Number(m.activityId),
        from: m.from as 'parent' | 'child',
        ...(m.parentId ? { parentId: String(m.parentId) } : {}),
        name: String(m.name),
        text: String(m.text),
        ts: Number(m.ts),
      };
      setMessages((prev) => (prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]));
    });
  }, [socket, child.id]);

  const online = child.devices.some((d) => d.online);
  return (
    <section className="card stack">
      <h2>Chat with {child.name}</h2>
      <p className="muted small">
        Send {child.name} a message — handy when live view or talking isn’t available.{' '}
        {online ? 'Their app is online now.' : `They’ll see it next time ${child.name}’s app is online.`}
      </p>
      {!connected && <p className="muted small">Reconnecting…</p>}
      <ChatThread messages={messages} onSend={send} meSide="parent" me={{ id: parent.id, name: parent.name }} placeholder={`Message ${child.name}…`} />
    </section>
  );
}
